import type { ApolloError } from '@apollo/client';
import type { DealPromptAudienceSetMetrics, DealPromptCumulativeDataItem } from '@openx/types/dealPrompt';
import type * as Types from '@openx/types/gqlTypes';
import {
  Gender,
  type NormalizedDefinitionGroup,
  NormalizedDefinitionOperator,
  SegmentSearchMode,
} from '@openx/types/gqlTypes';
import type { ConstValues } from '@openx/types/helpers';
import type { Option } from '@openx/types/options';

export type GetAccountListOptionsQueryVariables = {
  accountId?: Types.InputMaybe<Types.Scalars['uuid']['input']>;
  where?: Types.InputMaybe<Types.Account_List_Bool_Exp>;
};

export type CheckIfShowMyDataQueryVariables = {
  accountId: Types.InputMaybe<Types.Scalars['uuid']['input']>;
};

export const NormalizedDefinitionOperatorLabels = {
  [NormalizedDefinitionOperator.Intersect]: 'and',
  [NormalizedDefinitionOperator.Union]: 'or',
} as const;

type BaseGroup = {
  /**
   * id used for tracking expanded/collapsed state in the tree
   */
  uuid?: string;
  operator: NormalizedDefinitionOperator;
};

export type SegmentGroupItem<T> = { segments: T[]; operator: NormalizedDefinitionOperator };

/** Keys used to assign segments to group properties (aligned with audience form). */
export const SEGMENT_GROUP_CATEGORIES = {
  DIRECT: 'direct',
  LOCATION: 'location',
  LOOKALIKE: 'lookalike',
  PREDEFINED: 'predefined',
  DEMOGRAPHIC_AGE: 'demographic_age',
  DEMOGRAPHIC_GENDER: 'demographic_gender',
} as const;
export type SegmentGroupCategory = ConstValues<typeof SEGMENT_GROUP_CATEGORIES>;

export interface Group extends BaseGroup {
  demographic_age?: SegmentGroupItem<Types.AudienceSegmentEntry>;
  demographic_gender?: SegmentGroupItem<Types.AudienceSegmentEntry>;
  location?: SegmentGroupItem<Types.AudienceSegmentEntry>;
  direct?: SegmentGroupItem<Types.AudienceSegmentEntry>;
  predefined?: SegmentGroupItem<Types.AudienceSegmentEntry>;
  lookalike?: SegmentGroupItem<Types.AudienceSegmentEntry>;
}

export interface SubGroup extends BaseGroup {
  groups: any[];
}

export type NormalizeDefinitionType = {
  includes: { operator: NormalizedDefinitionOperator; groups: Omit<NormalizedDefinitionGroup, 'matched_keywords'>[] };
};

export type PricingOperator = 'INTERSECT' | 'UNION';

export type AudiencePricingResult = {
  computePricing: {
    pricing_by_org: Record<
      string,
      {
        price_pom: number;
        price_cpm: number;
      }
    >;
    operator: PricingOperator;
    totals: {
      price_pom: number;
      price_cpm: number;
    };
  };
};

export type DealPromptPricingQueryResult = {
  computePricing?: AudiencePricingResult['computePricing'] | null;
};

export const dealPromptIcons = {
  DOMAINS: 'domains',
  APPS: 'apps',
  AUDIENCE: 'audience',
  SCALE: 'scale',
  RELEVANCE: 'relevance',
  PERFORMANCE: 'performance',
} as const;
export type DealPromptIcon = ConstValues<typeof dealPromptIcons>;

export const dealPromptContextTypes = {
  ALL: 'all',
  DOMAINS: 'domains',
  APPS: 'apps',
  AUDIENCE: 'audience',
} as const;
export type DealPromptContextType = ConstValues<typeof dealPromptContextTypes>;

export const dealPromptFilterGoals = {
  SPEND: 'spend',
  IMPRESSIONS: 'impressions',
  CTR: 'ctr',
} as const;
export type DealPromptFilterGoal = ConstValues<typeof dealPromptFilterGoals>;

export const dealPromptFilterFormats = {
  BANNER: 'banner',
  VIDEO: 'video',
  NATIVE: 'native',
} as const;
export type DealPromptFilterFormat = ConstValues<typeof dealPromptFilterFormats>;

export const dealPromptFilterSegmentSearchMode = {
  ALL: 'All Segments',
  OXS_PROPRIETARY: SegmentSearchMode.OpenxProprietary,
  OXS_DATA_MARKETPLACE: SegmentSearchMode.Marketplace,
  MY_DATA: SegmentSearchMode.OwnOnly,
} as const;
export type DealPromptFilterSegmentSearchMode = ConstValues<typeof dealPromptFilterSegmentSearchMode>;

export const dealPromptFilterGender = {
  ANY: 'Any',
  MALE: Gender.Male,
  FEMALE: Gender.Female,
} as const;
export type DealPromptFilterGender = ConstValues<typeof dealPromptFilterGender>;

export const dealPromptFilterFormatWisspMap = {
  [dealPromptFilterFormats.BANNER]: 'display',
} as const;

export const dealPromptFilterPlatforms = {
  IOS: 'ios',
  ANDROID: 'android',
} as const;
export type DealPromptFilterPlatform = ConstValues<typeof dealPromptFilterPlatforms>;

export const dealPromptFilterPlacement = {
  INSTREAM: 'INSTREAM',
  ACCOMPANYING_CONTENT: 'ACCOMPANYING_CONTENT',
  INTERSTITIAL: 'INTERSTITIAL',
  NO_CONTENT_STANDALONE: 'NO_CONTENT_STANDALONE',
} as const;
export type DealPromptFilterPlacement = ConstValues<typeof dealPromptFilterPlacement>;

export const dealPromptFilterDevice = {
  PHONE: 'phone',
  MOBILE_TABLET: 'mobile/tablet',
  SET_TOP_BOX: 'set-top-box',
  TABLET: 'tablet',
  DESKTOP: 'desktop',
  CONNECTED_TV: 'connected tv',
} as const;
export type DealPromptFilterDevice = ConstValues<typeof dealPromptFilterDevice>;

export type DealPromptFilters = {
  state?: string[];
  dma?: string[];
  country?: string[];
  congressionalDistrict?: string[];
  phrase?: never;
  format?: DealPromptFilterFormat[];
  platform?: DealPromptFilterPlatform[];
  geography?: string[];
  goal?: DealPromptFilterGoal;
  ds_impressions?: string;
  ds_viewability?: string;
  ds_device?: string[];
  ds_video_placement?: string[];
  ds_content_vertical?: string[];
  ds_cpm?: string;
  user_location?: string[];
  account?: string[];
  category?: string[];
  sub_category?: string[];
  region?: string;
  demographic?: string[];
  segment_search_mode?: DealPromptFilterSegmentSearchMode;
  uniqueUsersReach?: string;
  age?: string[];
  gender?: Gender;
  ds_is_direct?: string[];
  format_and_devices?: never;
};

export type MetricKey = keyof DealPromptCumulativeDataItem['metrics'];
export type AudienceSegmentSetMetricKey = keyof DealPromptAudienceSetMetrics;

export const dealPromptFilterLocation = {
  STATE: 'state',
  DMA: 'dma',
  COUNTRY: 'country',
  CONGRESSIONAL_DISTRICT: 'congressional_district',
} as const;
export type DealPromptFilterLocation = ConstValues<typeof dealPromptFilterLocation>;

export const DEAL_PROMPT_US_REGION_CODE = 'US';

export type TaxonomyMetadata = {
  id: string;
  segment_source?: string | null;
  segment_type?: string | null;
  segment_category?: string | null;
  segment_sub_category?: string | null;
};

export type UseDealPromptLocationOptions = (skip?: boolean) => {
  loading: boolean;
  error?: ApolloError;
  data: DealPromptLocationOptions;
};

export type UseDealPromptRegionOptions = (skip?: boolean) => {
  loading: boolean;
  error?: ApolloError;
  data: Option[];
};

export type UseDealPromptCountryOptions = (
  region?: string,
  skip?: boolean
) => {
  loading: boolean;
  error?: ApolloError;
  data: Option[];
};

export type UseDealPromptDataPartnerOptions = (
  region?: string,
  countries?: string[],
  skip?: boolean
) => {
  loading: boolean;
  data: Option[];
};

export type UseDealPromptAudienceAccountOptions = (
  filters: GetAccountListOptionsQueryVariables,
  skip?: boolean
) => {
  loading: boolean;
  error?: ApolloError;
  data: Option[];
};

export type UseDealPromptTaxonomyMetadataOptions = (skip?: boolean) => {
  loading: boolean;
  error?: ApolloError;
  metadata: TaxonomyMetadata[];
};

export type DealPromptLocationOptions = {
  stateSegments: Option[];
  dmaSegments: Option[];
  congressionalSegments: Option[];
};

export type UseDealPromptAudienceExternalPartnerWithData = (
  filters?: CheckIfShowMyDataQueryVariables,
  skip?: boolean
) => {
  loading: boolean;
  error?: ApolloError;
  data: boolean;
};

export type UseDealPromptPricing = {
  fetchDealPromptPricing: (args: { accountId: string; normalizedDefinition: NormalizeDefinitionType }) => Promise<{
    data: DealPromptPricingQueryResult | undefined;
    error?: ApolloError;
  }>;
};

export type DealPromptOrganizationsById = Record<string, { id: number; name: string } | undefined>;

export type UseDealPromptOrganizationsList = {
  organizationsById: DealPromptOrganizationsById;
  loading: boolean;
};

export type DealPromptAudienceDealExportTargets = {
  cookie: boolean;
  ctv: boolean;
  device: boolean;
  id5: boolean;
  idl: boolean;
  ip: boolean;
};

export type DealPromptAudienceDealFormValues = {
  name: string;
  export_targets: DealPromptAudienceDealExportTargets;
};

export type DealPromptAudienceCreateDealSubmitPayload = {
  form: DealPromptAudienceDealFormValues;
  segmentSet: Types.AudienceSegmentSet;
  selectedSegments: Types.AudienceSegmentEntry[];
  audienceAccountIdOverride?: string;
  dealFormAccountUidOverride?: string;
};

export const GROUP_KEYS_FOR_SEGMENTS = [
  'direct',
  'location',
  'lookalike',
  'predefined',
  'demographic_age',
  'demographic_gender',
] as const satisfies ReadonlyArray<
  keyof Pick<
    NormalizedDefinitionGroup,
    'direct' | 'location' | 'lookalike' | 'predefined' | 'demographic_age' | 'demographic_gender'
  >
>;

export type DefinitionGroup = NormalizeDefinitionType['includes']['groups'][number];

export type AudienceCreateNormalizedDefinition = NormalizeDefinitionType & {
  excludes: {
    operator: NormalizedDefinitionOperator;
    groups: unknown[];
  };
};

export type BuildDealPromptAudienceInsertInputArgs = {
  userId?: string;
  accountId?: string;
  form: DealPromptAudienceDealFormValues;
  segmentSet: Types.AudienceSegmentSet;
  selectedSegments: Types.AudienceSegmentEntry[];
};

export const EXPORT_TARGET_KEYS: (keyof DealPromptAudienceDealFormValues['export_targets'])[] = [
  'cookie',
  'device',
  'idl',
  'ctv',
  'ip',
  'id5',
];

export type ResultsTileBadge = {
  text: string;
  color: string;
  bgColor: string;
};

export type ResultsTileStat = {
  label: string;
  value: string;
  valueColor?: string;
  tooltip?: string;
  dataTest?: string;
};

export type ResultsTileRightItem = {
  label: string;
  value: number;
  formattedValue?: string;
  dataTest?: string;
};
